import React, { useState } from 'react';

/**
 * Purpose: Ads listing filter sidebar (collapsible on mobile, sticky on desktop).
 * Inputs: search, category, sort, price range state and change handlers.
 * Outputs: Filter UI matching SocietyFilters mobile pattern.
 */
const AdFilters = ({
  searchTerm,
  onSearchChange,
  filters,
  categories,
  onCategoryChange,
  onSortChange,
  onPriceRangeChange,
  onClearFilters,
}) => {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const activeFilterCount =
    (searchTerm?.trim() ? 1 : 0) +
    (filters.category ? 1 : 0) +
    (filters.priceRange && filters.priceRange !== 'all' ? 1 : 0) +
    (filters.sortBy && filters.sortBy !== 'created_at' && filters.sortBy !== 'newest' ? 1 : 0);

  const fieldGap = 'mb-4 lg:mb-5';
  const labelCls = 'block text-xs font-medium text-gray-700 mb-1.5 lg:text-sm lg:mb-2';
  const inputCls =
    'w-full min-h-[44px] px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';

  const handleClear = () => {
    setMobileFiltersOpen(false);
    onClearFilters();
  };

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm lg:border lg:shadow-sm">
      <div className="flex items-center justify-between gap-3 px-3 py-2.5 lg:hidden">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900">Filter & Sort</h3>
          {activeFilterCount > 0 ? (
            <p className="text-xs text-gray-500">{activeFilterCount} active</p>
          ) : (
            <p className="text-xs text-gray-500">Tap to refine results</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setMobileFiltersOpen((open) => !open)}
          aria-expanded={mobileFiltersOpen}
          aria-controls="ad-filters-panel"
          className="shrink-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 min-h-[44px]"
        >
          {mobileFiltersOpen ? 'Hide' : 'Show filters'}
        </button>
      </div>

      <div
        id="ad-filters-panel"
        className={`${mobileFiltersOpen ? 'block' : 'hidden'} max-h-[min(70vh,560px)] overflow-y-auto border-t border-gray-100 px-3 pb-3 pt-3 lg:!block lg:max-h-none lg:overflow-visible lg:border-t-0 lg:p-6 lg:pt-6`}
      >
        <h3 className="mb-4 hidden text-lg font-semibold text-gray-900 lg:mb-5 lg:block">
          Filters
        </h3>

        <div className={fieldGap}>
          <label className={labelCls} htmlFor="ad-filter-search">
            Search
          </label>
          <input
            id="ad-filter-search"
            type="search"
            value={searchTerm}
            onChange={onSearchChange}
            placeholder="Search ads..."
            className={inputCls}
            autoComplete="off"
          />
        </div>

        <div className={fieldGap}>
          <label className={labelCls} htmlFor="ad-filter-category">
            Category
          </label>
          <select
            id="ad-filter-category"
            value={filters.category}
            onChange={onCategoryChange}
            className={inputCls}
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>

        <div className={fieldGap}>
          <label className={labelCls} htmlFor="ad-filter-sort">
            Sort By
          </label>
          <select
            id="ad-filter-sort"
            value={filters.sortBy}
            onChange={onSortChange}
            className={inputCls}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="price_low">Price: Low to High</option>
            <option value="price_high">Price: High to Low</option>
            <option value="title">Title A-Z</option>
            <option value="views">Most Views</option>
          </select>
        </div>

        <div className={fieldGap}>
          <label className={labelCls} htmlFor="ad-filter-price">
            Price Range
          </label>
          <select
            id="ad-filter-price"
            value={filters.priceRange}
            onChange={onPriceRangeChange}
            className={inputCls}
          >
            <option value="all">Any Price</option>
            <option value="0-5000">Under PKR 5,000</option>
            <option value="5000-25000">PKR 5,000 - 25,000</option>
            <option value="25000-50000">PKR 25,000 - 50,000</option>
            <option value="50000+">Above PKR 50,000</option>
          </select>
        </div>

        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={handleClear}
            className="w-full min-h-[44px] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
};

export default AdFilters;
